import {AsyncPipe, DatePipe} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardTitle,} from '@angular/material/card';
import {MatChip} from '@angular/material/chips';
import {MatDialog} from '@angular/material/dialog';
import {MatDivider} from '@angular/material/divider';
import {
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
} from '@angular/material/expansion';
import {MatIcon} from '@angular/material/icon';
import {
    MatList,
    MatListItem,
    MatListItemIcon,
    MatListItemLine,
    MatListItemMeta,
    MatListItemTitle,
} from '@angular/material/list';
import {MatProgressBar} from '@angular/material/progress-bar';
import {MatTooltip} from '@angular/material/tooltip';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {BillingService, Subscription} from '@app/admin/billing.service';
import {Client, ClientService} from '@app/admin/client.service';
import {DeleteDialogComponent} from '@app/admin/delete-dialog/delete-dialog.component';
import {StorageDialogComponent} from '@app/admin/storage-dialog/storage-dialog.component';
import {InvitationLinkComponent} from '@app/invitation/invitation-link/invitation-link.component';
import {InvitationService} from '@app/invitation/invitation.service';
import {InviteUsersButtonComponent} from '@app/invitation/invite-users-button/invite-users-button.component';
import {UpgradeDialogComponent} from '@app/landing/upgrade-dialog/upgrade-dialog.component';
import {BytesPipe} from '@app/shared/bytes.pipe';
import {Observable} from 'rxjs';
import {filter, mergeMap, switchMap, tap} from 'rxjs/operators';
import {environment} from 'src/environments/environment';
import {UserAdminComponent} from '../user-admin/user-admin.component';

@Component({
    selector: 'frog-billing-admin',
    templateUrl: './billing-admin.component.html',
    styleUrls: ['./billing-admin.component.scss'],
    imports: [
        MatChip,
        MatButton,
        MatDivider,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        MatIcon,
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatCardContent,
        MatCardActions,
        MatProgressBar,
        RouterLink,
        MatList,
        MatListItem,
        MatListItemIcon,
        MatListItemTitle,
        MatListItemLine,
        MatListItemMeta,
        MatIconButton,
        MatTooltip,
        InviteUsersButtonComponent,
        UserAdminComponent,
        AsyncPipe,
        DatePipe,
        BytesPipe,
        InvitationLinkComponent,
    ],
})
export class BillingAdminComponent implements OnInit {
    public client$: Observable<Client>;
    public billingInfo$: Observable<Subscription>;
    public total: number;
    public storagePending: boolean;
    public hosted = environment.hosted;

    constructor(
        private clientService: ClientService,
        private invitationService: InvitationService,
        private billingService: BillingService,
        private matDialog: MatDialog,
        private activatedRoute: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        this.dataRequest();
        this.activatedRoute.queryParams.subscribe((params) => {
            if (params.success === '1') {
                this.matDialog.open(UpgradeDialogComponent);
            }
        });
    }

    openStorageDialog(clientId: string) {
        const dialog = this.matDialog.open(StorageDialogComponent, {
            maxWidth: '900px',
            width: '100%',
            data: clientId,
        });
        dialog.afterClosed().subscribe((result) => {
            this.dataRequest();
        });
    }

    revokeInvitation(invitationId: string) {
        this.invitationService
            .revokeInvitation(invitationId)
            .pipe(mergeMap(() => this.clientService.get()))
            .subscribe();
    }

    resendInvitation(invitationId: string) {
        this.invitationService
            .resendInvitation(invitationId)
            .pipe(mergeMap(() => this.clientService.get()))
            .subscribe();
    }

    confirmCancel(clientId: string): void {
        this.matDialog
            .open(DeleteDialogComponent, { data: false })
            .afterClosed()
            .pipe(
                filter((result) => Boolean(result)),
                switchMap(() => this.clientService.cancel(clientId)),
            )
            .subscribe((result) => {});
    }

    sumArray(arr: number[]): number {
        return arr.reduce((previous, current) => previous + current, 0) / 100.0;
    }

    customerPortal() {
        this.billingService.openBillingPortal().subscribe((session) => {
            window.location.href = session.url;
        });
    }

    private dataRequest() {
        this.client$ = this.clientService.get().pipe(
            mergeMap(() => this.clientService.client),
            tap((client) => {
                this.billingInfo$ = this.billingService.getBillingInfo().pipe(
                    tap((billing) => {
                        this.total = this.sumArray(
                            billing.items.map((item) => item.unit_amount * item.quantity),
                        );
                    }),
                );
            }),
        );
    }
}
